import React, { useEffect, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { Camera, X, ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(-1);

  useEffect(() => {
    document.title = "Gallery · Barrhaven Dealswagon Outlet";
    api.get("/settings")
      .then((r) => setPhotos(r.data.store_gallery || []))
      .finally(() => setLoading(false));
  }, []);

  const open = (i) => setLightbox(i);
  const close = () => setLightbox(-1);
  const prev = (e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + photos.length) % photos.length); };
  const next = (e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % photos.length); };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="gallery-page">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="inline-flex items-center gap-2 bg-brand-navy text-brand-yellow px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-widest">
          <Camera className="h-3.5 w-3.5" /> Our store
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-brand-navy mt-4">
          Take a look around
        </h1>
        <p className="text-slate-600 mt-2">
          A peek inside the Barrhaven Dealswagon Outlet warehouse and drive-through.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-xl bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-20" data-testid="gallery-empty">
          <ImageIcon className="h-12 w-12 mx-auto text-slate-300" />
          <div className="font-heading text-xl text-brand-navy mt-4">Photos coming soon</div>
          <p className="text-slate-500 mt-1">We're putting the finishing touches on our store photos.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
          {photos.map((p, i) => (
            <button
              key={p.id}
              onClick={() => open(i)}
              data-testid={`gallery-thumb-${p.id}`}
              className="group relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl transition"
            >
              <img src={mediaUrl(p.url)} alt={p.caption || "Store photo"} loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              {p.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2.5 text-left">
                  <span className="text-white text-xs font-bold line-clamp-2">{p.caption}</span>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {lightbox >= 0 && photos[lightbox] && (
        <div className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4" onClick={close} data-testid="gallery-lightbox">
          <button onClick={close} data-testid="gallery-lightbox-close" className="absolute top-4 right-4 text-white/80 hover:text-white"><X className="h-7 w-7" /></button>
          {photos.length > 1 && (
            <>
              <button onClick={prev} data-testid="gallery-lightbox-prev" className="absolute left-4 text-white/80 hover:text-white"><ChevronLeft className="h-9 w-9" /></button>
              <button onClick={next} data-testid="gallery-lightbox-next" className="absolute right-4 text-white/80 hover:text-white"><ChevronRight className="h-9 w-9" /></button>
            </>
          )}
          <div className="max-w-4xl max-h-[85vh] text-center" onClick={(e) => e.stopPropagation()}>
            <img src={mediaUrl(photos[lightbox].url)} alt={photos[lightbox].caption || ""} className="max-h-[80vh] max-w-full rounded-lg object-contain mx-auto" />
            {photos[lightbox].caption && <div className="text-white mt-3 font-bold">{photos[lightbox].caption}</div>}
          </div>
        </div>
      )}
    </main>
  );
}
